/**
 * api.ts — Authenticated fetch wrapper + API helpers (RESEARCH Pattern 4 / AUTH-02)
 *
 * Behaviour:
 *  - Sources the Cognito session from authService.getSession() (Plan 07 wires the real SRP
 *    service here; the interface is unchanged from Plan 05).
 *  - Attaches the Cognito access token as Bearer when a session exists.
 *  - DEV mode + no session (Cognito pool unconfigured locally) → sends request without token;
 *    the API server accepts this via DEV_AUTH_BYPASS (D-16 / T-01-27).
 *    This path is guarded by import.meta.env.PROD — it is never active in production builds.
 *  - PROD + no session → redirect to /login (Pitfall 2: getSession returns null silently when
 *    both tokens are expired; we must redirect rather than send "Bearer undefined").
 *  - 401 response → call getSession() again (auto-refreshes via refresh token) and retry once.
 *    If the refresh also returns null → redirect to /login (session is irrecoverably expired).
 */
import { getSession } from '../auth/authService'
import type { SiteScoreResult, IsochroneResult, CensusTract } from './tractModel'

function withBearerToken(
  session: Awaited<ReturnType<typeof getSession>> & object,
  init: RequestInit,
): RequestInit {
  return {
    ...init,
    headers: {
      ...init.headers,
      Authorization: `Bearer ${session.getAccessToken().getJwtToken()}`,
    },
  }
}

/**
 * Authenticated fetch.
 *
 * Usage identical to the native fetch() — drop-in replacement for all API calls
 * that require a valid Cognito session.
 */
export async function authedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const session = await getSession()

  if (!session) {
    if (import.meta.env.PROD) {
      // Production: no valid session → force re-login (AUTH-01 / Pitfall 2)
      window.location.href = '/login'
      throw new Error('Not authenticated')
    }
    // DEV mode: Cognito pool not configured locally — send without token.
    // The API's DEV_AUTH_BYPASS accepts unauthenticated requests (D-16 / T-01-27).
    return fetch(url, init)
  }

  let res = await fetch(url, withBearerToken(session, init))

  // 401 retry: the access token may have just expired between getSession() and the request.
  // getSession() will auto-use the refresh token to obtain a new access token.
  if (res.status === 401) {
    const refreshed = await getSession()
    if (!refreshed) {
      // Refresh token also expired — session is gone, redirect to login (Pitfall 2)
      window.location.href = '/login'
      throw new Error('Session expired')
    }
    res = await fetch(url, withBearerToken(refreshed, init))
  }

  return res
}

// ─── Coverage helper (T-092, A3 — de-hardcode the 4-state ceiling) ───────────

/** One loaded region in the coverage response. */
export interface CoverageState {
  state_abbrev: string
  state_fips: string
}

/**
 * The app's self-description of which regions are loaded, from GET /api/coverage.
 * `extent` is the union bounding box [west, south, east, north], or null when no
 * regions are loaded. Drives the default map camera (A1) and the coverage banner (A2)
 * so adding a state needs no frontend code change.
 */
export interface Coverage {
  states: CoverageState[]
  extent: [number, number, number, number] | null
}

/**
 * Fetch the loaded-region coverage (states + union extent).
 *
 * GETs /api/coverage (authedFetch attaches the Cognito token). Throws on non-2xx so the
 * caller can fall back to the national default view.
 */
export async function fetchCoverage(): Promise<Coverage> {
  const res = await authedFetch('/api/coverage')
  if (!res.ok) {
    throw new Error(`Coverage request failed: ${res.status}`)
  }
  return res.json() as Promise<Coverage>
}

// ─── Authoritative tract-by-geoid (T-233) ────────────────────────────────────

/**
 * Fetch the FULL census tract detail for a geoid.
 *
 * GETs /api/tracts/:geoid (authedFetch attaches the Cognito token). The response is the
 * server's complete tract row (all 50 columns), key-for-key assignable to CensusTract —
 * the same authoritative source the map-click and search paths reach via /api/tracts/at.
 *
 * T-233: the ranked-list selection path carries a raw map-source feature snapshot that can be
 * incomplete (only the choropleth's `lvi` + `dominant_type` survive a mid-reload
 * `querySourceFeatures`), which renders a headline with every metric dashed. handleRankedSelect
 * calls this to re-hydrate the selection with server truth. Throws on non-2xx so the caller keeps
 * the optimistic tract instead of clearing it (degraded-but-present beats blank).
 */
export async function fetchTractByGeoid(geoid: string): Promise<CensusTract> {
  const res = await authedFetch(`/api/tracts/${encodeURIComponent(geoid)}`)
  if (!res.ok) {
    throw new Error(`Tract detail request failed: ${res.status}`)
  }
  return res.json() as Promise<CensusTract>
}

// ─── Gated tile transport (T-102) ────────────────────────────────────────────

/**
 * CloudFront signed-URL credentials for the aggregate-tier PMTiles pyramids, from
 * GET /api/tile-auth. Each URL is a time-limited bearer credential (auth in the query string);
 * `expiresAt` (epoch seconds) lets the client refresh before it lapses. See the T-102 topology
 * memo for why the transport is signed URLs rather than cookies.
 */
export interface TileAuth {
  expiresAt: number
  tiles: { state: string; county: string }
}

/**
 * Fetch signed pmtiles URLs for the state + county tiers.
 *
 * GETs /api/tile-auth (authedFetch attaches the Cognito token). Returns null when tile delivery
 * is unavailable — a 503 (distribution not wired or signing key missing) or any error — so the
 * caller falls back to the authenticated bbox-GeoJSON tiers. Never throws.
 */
export async function fetchTileAuth(): Promise<TileAuth | null> {
  try {
    const res = await authedFetch('/api/tile-auth')
    if (!res.ok) return null
    return (await res.json()) as TileAuth
  } catch {
    return null
  }
}

// ─── Site score + reverse-geocode helpers (Plan 02-04) ───────────────────────

/**
 * Fetch a site-level Opportunity Score for the given coordinates.
 *
 * POSTs { lat, lng } to POST /api/score/site (T-02-17: authedFetch attaches Cognito token).
 * Throws on non-2xx so the caller can surface error copy (SCORE-05).
 */
export async function fetchSiteScore(lat: number, lng: number): Promise<SiteScoreResult> {
  const res = await authedFetch('/api/score/site', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lat, lng }),
  })
  if (!res.ok) {
    throw new Error(`Site score request failed: ${res.status}`)
  }
  return res.json() as Promise<SiteScoreResult>
}

/**
 * Fetch a drive-time isochrone (reachable-area polygon) around a point.
 *
 * POSTs { lat, lng, minutes } to POST /api/isochrone (authedFetch attaches the Cognito
 * token). `minutes` is whitelisted server-side to 5/10/15. Throws on non-2xx so the
 * layer can surface a non-fatal error toast + Retry (T-135 §6). A 200 with
 * `geojson: null` (no reachable-area data) is a valid result, returned as-is.
 */
export async function fetchIsochrone(
  lat: number,
  lng: number,
  minutes: 5 | 10 | 15,
  signal?: AbortSignal,
): Promise<IsochroneResult> {
  const res = await authedFetch('/api/isochrone', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lat, lng, minutes }),
    signal,
  })
  if (!res.ok) {
    throw new Error(`Isochrone request failed: ${res.status}`)
  }
  return res.json() as Promise<IsochroneResult>
}

/**
 * Reverse-geocode a lat/lng to a human-readable display name.
 *
 * GETs /api/geocode/reverse?lat=&lng= (T-02-17: authedFetch attaches Cognito token).
 * Returns the displayName string, or null on failure (best-effort — never blocks scoring).
 */
export async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  try {
    const res = await authedFetch(`/api/geocode/reverse?lat=${lat}&lng=${lng}`)
    if (!res.ok) return null
    const data = (await res.json()) as { displayName?: string }
    return data.displayName ?? null
  } catch {
    return null
  }
}
